import React, { useState } from 'react'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { uploadFile, generateFilePath } from '../utils/firebaseStorage'

const { FiUpload, FiX, FiLoader, FiImage, FiMusic } = FiIcons

const FileUpload = ({ 
  type = 'image', 
  classNumber, 
  onUploadComplete, 
  accept = 'image/*', 
  multiple = false, 
  className = '' 
}) => {
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState([])

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return

    setUploading(true)
    setUploadProgress([])

    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        // Validate file type
        if (type === 'image' && !file.type.startsWith('image/')) {
          return { success: false, error: `${file.name} is not a valid image file` }
        }
        if (type === 'audio' && !file.type.startsWith('audio/')) {
          return { success: false, error: `${file.name} is not a valid audio file` }
        }

        // Validate file size (50MB limit)
        if (file.size > 50 * 1024 * 1024) {
          return { success: false, error: `${file.name} is too large (max 50MB)` }
        }

        try {
          const filePath = generateFilePath(classNumber, type, file.name)
          const result = await uploadFile(file, filePath)
          
          setUploadProgress(prev => {
            const newProgress = [...prev]
            newProgress[index] = { name: file.name, status: 'completed' }
            return newProgress
          })

          return result
        } catch (error) {
          setUploadProgress(prev => {
            const newProgress = [...prev]
            newProgress[index] = { name: file.name, status: 'error', error: error.message }
            return newProgress
          })
          return { success: false, error: error.message, fileName: file.name }
        }
      })

      const results = await Promise.all(uploadPromises)
      const successfulUploads = results.filter(r => r.success)
      
      if (successfulUploads.length > 0) {
        onUploadComplete(successfulUploads)
      }

      const failures = results.filter(r => !r.success)
      if (failures.length > 0) {
        console.error('Some files failed to upload:', failures)
        const errorMessages = failures.map(f => `${f.fileName || 'Unknown'}: ${f.error}`).join('\n')
        alert(`Some files failed to upload:\n${errorMessages}`)
      }

    } catch (error) {
      console.error('Upload failed:', error)
      alert('Upload failed: ' + error.message)
    } finally {
      setUploading(false)
      setTimeout(() => setUploadProgress([]), 2000)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const files = e.dataTransfer.files
    handleFileUpload(files)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const getTypeIcon = () => {
    return type === 'audio' ? FiMusic : FiImage
  }

  const getTypeText = () => {
    return type === 'audio' ? 'audio files' : 'images'
  }

  const getAcceptedFormats = () => {
    if (type === 'audio') return 'MP3, WAV, OGG, M4A'
    return 'JPEG, PNG, GIF, WebP'
  }

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
        dragOver 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-300 hover:border-gray-400'
      } ${className}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {uploading ? (
        <div className="space-y-3">
          <div className="flex items-center justify-center space-x-2 py-2">
            <SafeIcon icon={FiLoader} className="text-blue-500 animate-spin" />
            <span className="text-gray-600">Uploading {getTypeText()}...</span>
          </div>
          
          {uploadProgress.length > 0 && (
            <div className="space-y-1 text-sm">
              {uploadProgress.map((progress, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-600 truncate">{progress.name}</span>
                  <span className={`text-xs ${
                    progress.status === 'completed' ? 'text-green-600' : 
                    progress.status === 'error' ? 'text-red-600' : 'text-blue-600'
                  }`}>
                    {progress.status === 'completed' ? '✓' : 
                     progress.status === 'error' ? '✗' : '...'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <SafeIcon icon={getTypeIcon()} className="mx-auto text-gray-400 text-2xl mb-2" />
          <p className="text-gray-600 mb-1">
            Drag & drop {getTypeText()} here or click to browse
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Supported formats: {getAcceptedFormats()} (max 50MB)
          </p>
          
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
            id={`file-upload-${type}-${classNumber}-${Date.now()}`}
          />
          <label
            htmlFor={`file-upload-${type}-${classNumber}-${Date.now()}`}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition-colors"
          >
            <SafeIcon icon={FiUpload} />
            <span>Choose {getTypeText()}</span>
          </label>
        </>
      )}
    </div>
  )
}

export default FileUpload